/**
 * Màn Tổng quan — trả lời **ba câu, theo đúng thứ tự này**:
 *
 *   1. Có việc gì tôi phải làm không?
 *   2. Máy còn chạy không?
 *   3. Nó đã làm được gì?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ĐỢT 15b — dựng lại theo `Shopify-Colorizeyou/apps/web/src/routes/Dashboard.tsx`.
 *
 * Lynust: *"cách trình bày của auto IME rất rối, nhất là trang tổng quan"*.
 *
 * Bản trước là **một chồng card bằng nhau**: card "chạy một lượt", card "chờ duyệt", card "chi
 * phí", rồi **năm nhóm × mười hai ô số**. Autopilot gọi đúng tên lỗi đó:
 *
 *   > *"Bốn hộp bốn dòng trở thành một câu mỗi hàng đợi: **mười sáu con số mà mười bốn cái luôn
 *   > bằng 0 thì không phải thông tin**."*
 *
 * Ba luật dựng trang, chép nguyên:
 *
 * 1. **Trang là BA TẦNG, không phải một chồng hộp bằng nhau.** Thứ bậc phải nhìn thấy được trước
 *    khi đọc chữ.
 * 2. **Chỉ tầng 1 được dùng cỡ chữ lớn.** Con số bài chờ duyệt là con số **duy nhất** trên màn
 *    này ở cỡ `text-4xl`; mọi thứ khác nhỏ hơn hẳn. Tỉ lệ tự nó xếp hạng, không cần thêm một chữ
 *    trang trí nào.
 * 3. **Tầng khoẻ thì tự gập.** Hàng đợi rỗng không phải tin tức. Nhưng **kết luận cưỡi trên hàng
 *    đóng** — gập lại không bao giờ giấu mất điều duy nhất mà vùng đó tồn tại để nói.
 *
 * `AP-12` vẫn giữ: **mọi trạng thái đều có bộ đếm**, và màn này tự kiểm đủ 12 khoá. Chúng nằm
 * trong khối "Chi tiết từng trạng thái" đóng sẵn — đếm đủ, nhưng không đổ cả mười hai con số lên
 * mặt trang khi mười cái đang bằng 0.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api, type TinCanhBao, type TongQuan as DuLieu, type TrangThaiBai } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN, nhanTrangThai } from "../lib/nhan.ts";
import {
  Bao,
  Chip,
  DangTai,
  DauThe,
  DauTrang,
  Doc,
  HuyHieuLive,
  Khoi,
  LuoiDoc,
  Nut,
  The,
} from "../thanh-phan/giao-dien.tsx";
import {
  BtCanhBao,
  BtDung,
  BtDungTron,
  BtLoi,
  BtMuiPhai,
  BtQuay,
} from "../thanh-phan/bieu-tuong.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

/**
 * Mười hai trạng thái gộp thành **sáu** con số.
 *
 * Sáu vì đó là số câu hỏi thật sự khác nhau. Mười hai ô riêng không cho biết thêm gì mà mười cái
 * trong đó luôn bằng 0 — chi tiết vẫn còn, nằm trong khối gập ở cuối trang.
 */
const GOP: { nhan: string; trangThai: TrangThaiBai[]; den?: string }[] = [
  { nhan: "chờ duyệt", trangThai: ["PENDING_REVIEW"], den: "/hang-cho" },
  { nhan: "đã từ chối", trangThai: ["REJECTED_TEXT", "REJECTED_IMAGE"], den: "/hang-cho" },
  { nhan: "máy đang làm", trangThai: ["BRIEF_READY", "WRITING", "NEEDS_IMAGE"] },
  { nhan: "đã lên lịch", trangThai: ["APPROVED", "SCHEDULED"], den: "/lich" },
  { nhan: "đã đăng", trangThai: ["PUBLISHED_WEB", "PUBLISHED_ALL"], den: "/lich" },
  { nhan: "hỏng / đã bỏ", trangThai: ["FAILED", "DISCARDED"] },
];

/** Nhóm cho khối chi tiết — vẫn đủ 12, chỉ không nằm trên mặt trang. */
const NHOM_CHI_TIET: { ten: string; trangThai: TrangThaiBai[] }[] = [
  { ten: "Chờ người", trangThai: ["PENDING_REVIEW", "REJECTED_TEXT", "REJECTED_IMAGE"] },
  { ten: "Máy đang làm", trangThai: ["BRIEF_READY", "WRITING", "NEEDS_IMAGE"] },
  { ten: "Sắp đăng", trangThai: ["APPROVED", "SCHEDULED"] },
  { ten: "Đã đăng", trangThai: ["PUBLISHED_WEB", "PUBLISHED_ALL"] },
  { ten: "Kết thúc", trangThai: ["FAILED", "DISCARDED"] },
];

export function TongQuan() {
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isPending, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: khoa.tongQuan,
    queryFn: api.tongQuan,
    refetchInterval: 30_000,
  });

  // Model nào đang chạy — hỏi máy chủ, không đoán. Khác biệt giữa "$0 vì model đóng thế" và
  // "$0 vì chưa gọi lần nào" là khác biệt quan trọng nhất trên màn này.
  const { data: aiModel } = useQuery({ queryKey: ["ai-dang-dung"], queryFn: api.aiDangDung });
  const { data: lich } = useQuery({ queryKey: khoa.lich, queryFn: () => api.lich(100) });

  const chay = useMutation({
    mutationFn: api.chayMotLuot,
    onSuccess: (kq) => {
      // `daChay === 0` là một kết quả **hợp lệ và hay gặp** (hàng đợi rỗng), không phải lỗi — nên
      // nó vẫn là toast xanh, chỉ khác lời. Tô nó thành lỗi là dạy người đọc bỏ qua toast đỏ.
      toast.on(
        kq.daChay > 0 ? `Đã xử lý ${kq.daChay} job` : "Hàng đợi rỗng — không có job nào để chạy",
        kq.ghiChu,
      );
      void qc.invalidateQueries({ queryKey: khoa.tongQuan });
      void qc.invalidateQueries({ queryKey: khoa.hangCho });
      void qc.invalidateQueries({ queryKey: khoa.lich });
    },
    onError: (loi: Error) => toast.loi("Chạy một lượt hỏng", loi.message),
  });

  if (error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Tổng quan" />
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
          <button onClick={() => refetch()} className="ml-2 underline">
            Thử lại
          </button>
        </Bao>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Tổng quan" />
        <DangTai />
      </div>
    );
  }

  const d: DuLieu = data;
  const choDuyet = d.demTheoTrangThai.PENDING_REVIEW ?? 0;
  const soKhoa = Object.keys(d.demTheoTrangThai).length;
  const nghiemTrong = d.canhBaoGanDay.filter((c) => c.muc === "nghiem-trong");
  const sapDang = (lich?.muc ?? []).filter((m) => !m.daChay).sort((a, b) => a.gioDang.localeCompare(b.gioDang));
  const quaHan = lich?.quaHan ?? 0;
  const modelThat = aiModel?.["viet-bai"]?.tonTien === true;

  return (
    <div className="min-w-0 space-y-6">
      <DauTrang
        tieuDe="Tổng quan"
        trangThai={<HuyHieuLive live={d.cronBat && modelThat} />}
        moTa={
          d.chuaCoBaiNao
            ? "Hệ chưa chạy lần nào — đây không phải lỗi."
            : `${d.tongBai} bài trong sổ · cập nhật ${gioVN(new Date(dataUpdatedAt).toISOString())}`
        }
        chiTiet="Mọi con số trên màn này đọc thẳng từ database, không qua bộ nhớ đệm — nên chúng đúng tại đúng thời điểm ghi ở trên, và tự tải lại mỗi 30 giây."
      />

      {/* ══ TẦNG 1 — Có việc gì cần tôi? ═══════════════════════════════════
          Vùng DUY NHẤT trên màn được dùng cỡ chữ lớn. */}
      <section aria-labelledby="tang-can-toi" className="space-y-3">
        <h2 id="tang-can-toi" className="sr-only">
          Cần anh xử lý
        </h2>
        <CanToi choDuyet={choDuyet} canhBao={nghiemTrong} quaHan={quaHan} luc={dataUpdatedAt} />

        {/*
          Dữ liệu mẫu phải TỰ KHAI, ngay cạnh những con số nó đang làm sai lệch.

          Lynust từng báo "có bài bị lỗi" — hoá ra là `mau-failed-10`, một bài FAILED do `seed-mau`
          cố ý tạo để kiểm AP-12. Từ màn này nó giống hệt một sự cố thật. Một cảnh báo giả không
          chỉ tốn một lần hỏi: nó dạy người xem CÁCH BỎ QUA cảnh báo. Xem `checklist.md` B8.
        */}
        {d.soBaiMau > 0 && (
          <Bao muc="thuong" tieuDe={`${d.soBaiMau} trong ${d.tongBai} bài là DỮ LIỆU MẪU`}>
            Do lệnh <code className="text-2xs">seed-mau</code> tạo để nhìn thử giao diện. Chúng nằm
            trong mọi bộ đếm bên dưới — kể cả ô hỏng và ô chờ duyệt. Xoá sạch:{" "}
            <code className="text-2xs">pnpm --filter @autoime/api seed-mau --xoa</code>
          </Bao>
        )}
      </section>

      {/* ══ TẦNG 2 — Việc gần ══════════════════════════════════════════════ */}
      <div className="grid min-w-0 gap-4 lg:grid-cols-3">
        <The className="lg:col-span-2">
          <DauThe tieuDe="Sắp đăng" phu={`${sapDang.length} dòng lịch chưa chạy`}>
            <Link to="/lich" className="shrink-0 text-2xs text-muc-500 underline hover:text-muc-900">
              xem lịch
            </Link>
          </DauThe>

          {sapDang.length === 0 ? (
            <p className="text-xs text-muc-500">
              Không có gì đang chờ đăng. Duyệt một bài ở Hàng chờ thì nó xuất hiện ở đây.
            </p>
          ) : (
            <ul className="min-w-0 divide-y divide-muc-200">
              {sapDang.slice(0, 5).map((m) => (
                <li key={m._id} className="flex min-w-0 items-center gap-2 py-2 first:pt-0 last:pb-0">
                  <span className="min-w-0 flex-1 truncate text-sm text-muc-800" title={m.tieuDe ?? ""}>
                    {m.tieuDe ?? "(bài đã bị xoá)"}
                  </span>
                  <Chip muc="thuong">{m.siteSlug ?? "?"}</Chip>
                  {m.cheDoThucTe === "live" && <Chip muc="loi">thật</Chip>}
                  <span className="shrink-0 so-thang-cot text-2xs text-muc-500">
                    {gioVN(m.gioDang)}
                  </span>
                </li>
              ))}
              {sapDang.length > 5 && (
                <li className="pt-2 text-2xs text-muc-500">
                  …và {sapDang.length - 5} dòng nữa
                </li>
              )}
            </ul>
          )}
        </The>

        <The>
          <DauThe
            tieuDe="Chạy dây chuyền"
            // Câu này ĐỌC trạng thái cron, không nhớ nó. Bản trước ghi cứng "Cron đang TẮT" —
            // đúng vào ngày viết, và thành lời nói dối ngay hôm bật cron.
            phu={d.cronBat ? "cron đang bật — nút này chạy sớm hơn" : "cron đang TẮT"}
          />

          {aiModel !== undefined && (
            <p className="mb-3 text-2xs text-muc-600">
              Model viết bài: <code className="text-muc-800">{aiModel["viet-bai"]?.catalogKey ?? "?"}</code>
              <br />
              {modelThat ? (
                <span className="font-medium text-loi-600">TIÊU TIỀN THẬT</span>
              ) : (
                <span className="text-on-700">model đóng thế · $0 · không chạm mạng</span>
              )}
            </p>
          )}

          {/* Nút VIỀN, không phải cam: nút cam duy nhất của màn nằm ở tầng 1. */}
          <Nut kieu="vien" dangChay={chay.isPending} onClick={() => chay.mutate()} className="w-full">
            Chạy một lượt
          </Nut>
          {/* Kết quả đi bằng toast — xem `onSuccess`. Một dòng chữ nhỏ nằm dưới nút, trong một
              card ở cột phải, là chỗ mắt KHÔNG quay lại sau khi bấm: người bấm nhìn vào nút. */}
        </The>
      </div>

      {/* ══ TẦNG 3 — Máy đã làm được gì ════════════════════════════════════
          Đường kẻ có nhãn, KHÔNG phải một panel nữa. Nó tốn một dòng và nói được "một loại thứ
          khác bắt đầu từ đây" mà không tự nhận mình là một đối tượng — thứ giữ cho trang dài
          không đọc thành một đống hộp bằng nhau. */}
      <Khoi
        nhan="Dây chuyền"
        id="tq-day-chuyen"
        moSan
        tomTat={
          d.chuaCoBaiNao ? (
            "chưa có bài nào"
          ) : (
            <span className="text-muc-500">
              {d.tongBai} bài · ${d.chiPhi.thangUsd.toFixed(2)} tháng này
            </span>
          )
        }
      >
        <LuoiDoc cot={6}>
          {GOP.map((g) => {
            const n = g.trangThai.reduce((t, x) => t + (d.demTheoTrangThai[x] ?? 0), 0);
            const laChoDuyet = g.nhan === "chờ duyệt";
            const laHong = g.nhan === "hỏng / đã bỏ";
            return (
              <Doc
                key={g.nhan}
                nhan={g.nhan}
                giaTri={n}
                // Cam CHỈ cho "chờ duyệt khác 0" — trạng thái duy nhất ở đây thật sự cần anh.
                canAnh={laChoDuyet && n > 0}
                sacThai={laHong && n > 0 ? "xau" : undefined}
                den={n > 0 ? g.den : undefined}
              />
            );
          })}
        </LuoiDoc>

        <div className="grid min-w-0 gap-2.5 sm:grid-cols-2">
          <Doc
            nhan="chi phí hôm nay (giờ VN)"
            giaTri={`$${d.chiPhi.ngayUsd.toFixed(2)}`}
            ghiChu={
              d.chiPhi.thangUsd === 0
                ? "$0 là con số ĐÃ ĐO, không phải chưa đo"
                : "sổ cái ghi cả lời gọi lỗi (AP-45)"
            }
            den="/chi-phi"
          />
          <Doc
            nhan="chi phí tháng này (giờ VN)"
            giaTri={`$${d.chiPhi.thangUsd.toFixed(2)}`}
            den="/chi-phi"
          />
        </div>
      </Khoi>

      {/* Đủ 12 bộ đếm vẫn ở đây — AP-12 giữ nguyên. Chỉ là chúng không đổ lên mặt trang khi mười
          cái đang bằng 0. Đóng sẵn: mở ra là một hành động có chủ đích. */}
      <Khoi
        nhan="Chi tiết từng trạng thái"
        id="tq-chi-tiet"
        tomTat={
          soKhoa === 12 ? (
            <span className="flex items-center gap-1.5 text-on-600">
              <BtDung className="h-3.5 w-3.5 shrink-0" />
              đủ 12/12 bộ đếm
            </span>
          ) : (
            <span className="flex items-center gap-1.5 font-medium text-loi-600">
              <BtCanhBao className="h-3.5 w-3.5 shrink-0" />
              chỉ {soKhoa}/12 bộ đếm
            </span>
          )
        }
      >
        {soKhoa !== 12 && (
          <Bao muc="loi" tieuDe={`API chỉ trả ${soKhoa}/12 bộ đếm`}>
            Một trạng thái không ai đếm là một trạng thái xác sống (AP-12). Cách nó xảy ra: thêm
            trạng thái mới mà quên thêm bộ đếm.
          </Bao>
        )}

        {NHOM_CHI_TIET.map((nhom) => (
          <div key={nhom.ten} className="min-w-0 space-y-1.5">
            <p className="text-2xs font-medium uppercase tracking-[0.1em] text-muc-500">
              {nhom.ten}
            </p>
            <LuoiDoc cot={3}>
              {nhom.trangThai.map((t) => (
                <Doc key={t} nhan={nhanTrangThai(t).chu} giaTri={d.demTheoTrangThai[t] ?? 0} />
              ))}
            </LuoiDoc>
          </div>
        ))}
      </Khoi>
    </div>
  );
}

/**
 * **Tầng 1** — vùng duy nhất trên màn được dùng cỡ chữ lớn.
 *
 * Ba trạng thái, và ba cách hiện hoàn toàn khác nhau:
 *
 * - **Yên** — một dòng xanh nói *"không có gì cần anh"*. Đây là câu trả lời **thật**, không phải
 *   một ô trống. Một hộp viền rỗng ở chỗ này đọc ra là "chưa tải được".
 * - **Có việc** — card trắng, **đầu card** phủ cam nhạt, KHÔNG phủ cam cả khối. Sắc cam đánh dấu
 *   **đúng một** việc cần làm chứ không tô màu mọi thứ quanh nó.
 * - **Có cảnh báo** — chúng là những hàng thường trên nền trắng của card, **không phải một bức
 *   tường đỏ** đè lên mọi số liệu khác.
 */
function CanToi({
  choDuyet,
  canhBao,
  quaHan,
  luc,
}: {
  choDuyet: number;
  canhBao: TinCanhBao[];
  quaHan: number;
  luc: number;
}) {
  const coViec = choDuyet > 0;
  const yen = !coViec && canhBao.length === 0 && quaHan === 0;

  if (yen) {
    return (
      <div className="flex items-center gap-3 rounded-card border border-muc-300 bg-muc-0 p-5 shadow-card">
        <BtDungTron className="h-6 w-6 shrink-0 text-on-600" />
        <div className="min-w-0">
          <p className="text-base font-medium text-muc-900">Không có gì cần anh</p>
          <p className="text-xs text-muc-500">
            Hàng chờ trống, không có cảnh báo, lịch không kẹt · kiểm lúc{" "}
            {gioVN(new Date(luc).toISOString())}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 overflow-hidden rounded-card border border-canh-200 bg-muc-0 shadow-card">
      {coViec && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 bg-canh-500/8 px-5 py-4">
          {/*
            Con số DUY NHẤT trên màn được phép lớn thế này. Mọi thứ khác nhỏ hơn hẳn, nên tỉ lệ
            tự nó xếp hạng — không cần thêm một chữ trang trí nào.
          */}
          <span className="so-thang-cot text-4xl font-semibold leading-none text-canh-600 sm:text-5xl">
            {choDuyet}
          </span>
          <span className="min-w-0 flex-1 text-base text-muc-800">bài đang chờ anh duyệt</span>
          {/* Nút cam DUY NHẤT của cả màn. Tràn ngang trên điện thoại — đó là thứ anh mở app để bấm. */}
          <Link
            to="/hang-cho"
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-btn bg-canh-500 px-5 text-sm font-semibold text-canh-fg transition-colors hover:bg-canh-hover sm:w-auto"
          >
            Mở hàng chờ
            <BtMuiPhai className="h-4 w-4" />
          </Link>
        </div>
      )}

      {(canhBao.length > 0 || quaHan > 0) && (
        <ul className={coViec ? "border-t border-muc-200" : ""}>
          {quaHan > 0 && (
            <li>
              <Link
                to="/lich"
                className="flex min-w-0 items-start gap-2.5 px-5 py-3 text-sm transition-colors hover:bg-muc-100"
              >
                <BtLoi className="mt-0.5 h-4 w-4 shrink-0 text-loi-600" />
                <span className="min-w-0 flex-1">
                  <strong>{quaHan}</strong> dòng lịch quá giờ mà chưa chạy — bài đang kẹt.
                </span>
                <BtMuiPhai className="mt-0.5 h-4 w-4 shrink-0 text-muc-400" />
              </Link>
            </li>
          )}
          {canhBao.slice(0, 5).map((c, i) => (
            <li key={i} className="flex min-w-0 items-start gap-2.5 px-5 py-3 text-sm">
              <BtCanhBao className="mt-0.5 h-4 w-4 shrink-0 text-loi-600" />
              <span className="min-w-0 flex-1 break-words">
                <span className="font-mono text-2xs text-muc-500">[{c.nguon}]</span> {c.noiDung}
              </span>
            </li>
          ))}
          {canhBao.length > 0 && (
            <li className="flex items-start gap-2.5 border-t border-muc-200 px-5 py-2 text-2xs text-muc-500">
              <BtQuay className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-0" />
              <span>
                Cảnh báo chỉ nằm trong bộ nhớ tiến trình — <strong>mất sạch khi restart</strong>.
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
